import { makeStyles } from '@material-ui/core';
import { eachDayOfInterval, eachMonthOfInterval, isSameDay, startOfDay, startOfMonth } from 'date-fns';
import React from 'react';
import { theme } from '../../../theme/theme';
import { ProjectSummary } from '../../../types/project';
import { Report } from '../../../types/report';
import { BarChart } from '../../global/charts/BarChart';

const useMetricsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 20,
        '& > span': {
            color: theme.colors.neutral[1],
            ...theme.fonts.h5,
        },
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0px 1px 0px 0px #A2BAC3',
        '& > div:first-child': {
            display: 'flex',
            gap: 2,
            paddingBottom: 2,
            backgroundColor: theme.colors.neutral[6],
        },
        '& > div:first-child > div': {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 24,
            flex: 1,
            backgroundColor: 'white',
        },
        '& > div:first-child > div > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.fonts.h1,
        },
        '& > div:first-child > div > span:last-child': {
            color: theme.colors.neutral[2],
            ...theme.fonts.h6,
        },
    },
});

export const Metrics = (props: {
    loading?: boolean;
    report?: Report;
    forecasted: ProjectSummary[];
    scheduled: ProjectSummary[];
}) => {
    const classes = useMetricsClasses();
    const [interval, setInterval] = React.useState<Interval>();
    const [tick] = React.useState<'day' | 'month'>('month');
    const metrics = React.useMemo(
        () =>
            !props.loading && props.report && interval
                ? computeMetrics(props.report, props.forecasted, props.scheduled, interval, tick)
                : undefined,
        [props.loading, props.report, props.forecasted, props.scheduled, interval, tick],
    );

    console.log(metrics);

    React.useLayoutEffect(
        () =>
            setInterval({
                start: props.report?.start_date ?? new Date(),
                end: props.report?.end_date ?? new Date(),
            }),
        [props.report],
    );

    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    return (
        <div className={classes.root}>
            <span>Inspections Forecasting</span>
            <div className={classes.card}>
                <div>
                    <div>
                        <span>{metrics?.assetCodes.size}</span>
                        <span>ASSETS</span>
                    </div>
                    <div>
                        <span>{metrics?.projectsScheduled}</span>
                        <span>INSPECTIONS SCHEDULED</span>
                    </div>
                    <div>
                        <span>{`${Math.floor((metrics?.adherence ?? 0) * 100)}%`}</span>
                        <span>SCHEDULE ADHERENCE</span>
                    </div>
                    <div>
                        <span>{Math.round(metrics?.averagePerMonth ?? 0)}</span>
                        <span>AVER. INSPECTIONS PER MONTH</span>
                    </div>
                </div>
                <div>
                    <BarChart
                        style={{ height: 460 }}
                        type='grouped'
                        title='TOTAL INSPECTIONS SCHEDULED PER MONTH'
                        index={metrics?.reportMonths.map(date => months[date.getMonth()]) ?? []}
                        series={[
                            {
                                label: 'ASSETS DUE',
                                color: ['#6260B1'],
                                values: metrics?.forecastedPerMonth?.map(projects => projects.length) ?? [],
                            },
                            {
                                label: 'TOTAL INSPECTIONS SCHEDULED',
                                color: ['#A3A1CE'],
                                values: metrics?.scheduledPerMonth?.map(projects => projects.length) ?? [],
                            },
                        ]}
                    />
                </div>
            </div>
            <span>Inspection Completion Progress</span>
            <div className={classes.card}>
                <div>
                    <div>
                        <span>{(metrics?.scheduled.length ?? 0) - (metrics?.completedProjects ?? 0)}</span>
                        <span>INSPECTIONS DUE</span>
                    </div>
                    <div>
                        <span>{metrics?.completedProjects}</span>
                        <span>INSPECTIONS COMPLETE</span>
                    </div>
                    <div>
                        <span>{`${Math.floor((metrics?.completionRate ?? 0) * 100)}%`}</span>
                        <span>INSPECTIONS COMPLETION RATE</span>
                    </div>
                    <div>
                        <span>{metrics?.projectsDueToday.length}</span>
                        <span>INSPECTIONS DUE TODAY</span>
                    </div>
                </div>
                <div>
                    <BarChart
                        style={{ height: 460 }}
                        type='stacked'
                        title='INSPECTION PROGRESS'
                        index={metrics?.reportMonths.map(date => date.getMonth().toString()) ?? []}
                        series={[
                            {
                                label: 'SCHEDULED',
                                color: ['#E5F0FA'],
                                values:
                                    metrics?.scheduledPerMonth?.map(
                                        projects => projects.filter(p => p.status === 'ready to inspect').length,
                                    ) ?? [],
                            },
                            {
                                label: 'IN PROGRESS',
                                color: ['#0F8FFF'],
                                values:
                                    metrics?.scheduledPerMonth?.map(
                                        projects =>
                                            projects.filter(
                                                p => p.status !== 'ready to inspect' && p.status !== 'completed',
                                            ).length,
                                    ) ?? [],
                            },
                            {
                                label: 'COMPLETED',
                                color: ['#0A6FDB'],
                                values:
                                    metrics?.scheduledPerMonth?.map(
                                        projects => projects.filter(p => p.status === 'completed').length,
                                    ) ?? [],
                            },
                        ]}
                    />
                </div>
            </div>
        </div>
    );
};

export const computeMetrics = (
    report: Report,
    forecasted: ProjectSummary[],
    scheduled: ProjectSummary[],
    interval: Interval,
    tick: 'day' | 'month',
) => {
    const assetCodes = new Set<string>();
    forecasted.forEach(project => assetCodes.add(project.code));
    scheduled.forEach(project => assetCodes.add(project.code));
    let completedProjects = 0;
    let adherentProjects = 0;
    scheduled.forEach(project => {
        if (project.status !== 'completed') return;
        completedProjects += 1;
        adherentProjects += +(+startOfDay(project.completed_date!) <= +startOfDay(project.estimated_completed_date!));
    });
    //
    const reportMonths = eachMonthOfInterval({ start: report.start_date, end: report.end_date });
    const monthIndices = reportMonths.reduce(
        (acc, month, i) => ((acc[+month] = i), acc),
        {} as { [month: number]: number },
    );
    const forecastedPerMonth: ProjectSummary[][] = reportMonths.map(() => []);
    forecasted.forEach(project => {
        const internalMonth = project.internal_activity_required ? startOfMonth(project.internal_due_date!) : undefined;
        const externalMonth = project.external_activity_required ? startOfMonth(project.external_due_date!) : undefined;
        if (internalMonth && monthIndices[+internalMonth] != undefined)
            forecastedPerMonth[monthIndices[+internalMonth]].push(project);
        if (externalMonth && monthIndices[+externalMonth] != undefined)
            forecastedPerMonth[monthIndices[+externalMonth]].push(project);
    });
    const scheduledPerMonth: ProjectSummary[][] = reportMonths.map(() => []);
    scheduled.forEach(project => {
        const month = startOfMonth(project.estimated_completed_date!);
        if (monthIndices[+month] != undefined) scheduledPerMonth[monthIndices[+month]].push(project);
    });
    const averagePerMonth =
        reportMonths.reduce((acc, _, i) => acc + forecastedPerMonth[i].length + scheduledPerMonth[i].length, 0) /
        reportMonths.length;
    //
    const eachOfInterval = tick === 'day' ? eachDayOfInterval : eachMonthOfInterval;
    const start = tick === 'day' ? startOfDay : startOfMonth;
    const intervalDates = eachOfInterval({ start: start(interval.start), end: start(interval.end) });
    const intervalIndices = intervalDates.reduce(
        (acc, month, i) => ((acc[+month] = i), acc),
        {} as { [month: number]: number },
    );

    const forecastedPerInterval: ProjectSummary[][] = reportMonths.map(() => []);
    forecasted.forEach(project => {
        const internal = project.internal_activity_required ? start(project.internal_due_date!) : undefined;
        const external = project.external_activity_required ? start(project.external_due_date!) : undefined;
        if (internal && intervalIndices[+internal] != undefined)
            forecastedPerMonth[intervalIndices[+internal]].push(project);
        if (external && intervalIndices[+external] != undefined)
            forecastedPerMonth[intervalIndices[+external]].push(project);
    });
    const scheduledPerInterval: ProjectSummary[][] = reportMonths.map(() => []);
    scheduled.forEach(project => {
        const date = startOfMonth(project.estimated_completed_date!);
        if (intervalIndices[+date] != undefined) scheduledPerInterval[intervalIndices[+date]].push(project);
    });
    const averagePerInterval =
        intervalDates.reduce((acc, _, i) => acc + forecastedPerInterval[i].length + scheduledPerInterval[i].length, 0) /
        intervalDates.length;

    const projectsDueToday = scheduled.filter(
        project => project.status != 'completed' && isSameDay(project.estimated_completed_date!, new Date()),
    );

    return {
        forecasted,
        scheduled,
        assetCodes,
        assetCount: assetCodes.size,
        projectsForecasted: forecasted.length,
        projectsScheduled: scheduled.length,
        completedProjects,
        adherentProjects,
        adherence: adherentProjects / completedProjects,
        completionRate: completedProjects / scheduled.length,
        fullCompletionRate: (completedProjects + forecasted.length) / (scheduled.length + forecasted.length),
        //
        reportMonths,
        forecastedPerMonth,
        scheduledPerMonth,
        averagePerMonth,
        //
        intervalDates,
        forecastedPerInterval,
        scheduledPerInterval,
        averagePerInterval,
        //
        projectsDueToday,
    };
};
